import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { CustomProvider } from 'rsuite';
import enGB from 'rsuite/locales/en_GB';
import locales from './locales';
import Frame from './components/Frame';
import DashboardPage from './pages/dashboard';
import Error404Page from './pages/authentication/404';
import Error403Page from './pages/authentication/403';
import Error500Page from './pages/authentication/500';
import Error503Page from './pages/authentication/503';

import SignInPage from './pages/authentication/sign-in';
import UsersPage from './pages/user/list';
import UserDetailPage from './pages/user/detail/Index';
import ActivitiesPage from './pages/activity-info/list';
import ActivityDetailPage from './pages/activity-info/detail';
import ActivityReportsPage from './pages/activity-report/list';
import ActivityReportDetailPage from './pages/activity-report/detail';
import AchievementsPage from './pages/achievement/list';
import AchievementDetailPage from './pages/achievement/detail';
import RunEventsPage from './pages/run-event/list';
import RunEventDetailPage from './pages/run-event/detail';
import RunEventDistanceDetailPage from './pages/run-event/distance';
import RunEventKeysPage from './pages/run-event/run-event-key';
import ContinentsPage from './pages/master-data/continents';
import CountriesPage from './pages/master-data/countries/list';
import CountryDetailPage from './pages/master-data/countries/detail';
import DiscoveryPointsPage from './pages/discovery-point/list';
import DiscoveryPointDetailPage from './pages/discovery-point/detail';
import RoutesPage from './pages/route/list';
import RouteDetailPage from './pages/route/detail';
import RunningClubsPage from './pages/running-club/list';
import RunningClubDetailPage from './pages/running-club/detail';
import RunningClubEventDetailPage from './pages/running-club/running-club-event/detail';
import RunningClubEventsPage from './pages/running-club/running-club-event/list';
import ClubTagsPage from './pages/running-club/club-tag';
import StoriesPage from './pages/story/list';
import StoryDetailPage from './pages/story/detail';
import StoryChapterDetailPage from './pages/story/story-chapter/detail';
import StoryChapterMediaDetailPage from './pages/story/story-chapter-media/detail';
import StoryChapterTaskDetailPage from './pages/story/story-chapter-task/detail';
import CitiesPage from './pages/master-data/cities/list';
import CityDetailPage from './pages/master-data/cities/detail';
import CityDistrictDetailPage from './pages/master-data/cities/city-district';
import CityMultipleDistrictPage from './pages/master-data/cities/city-multiple-district';
import EncyclopediaItemsPage from './pages/encyclopedia/item/list';
import EncyclopediaItemDetailPage from './pages/encyclopedia/item/detail';
import ScrapedEventsPage from './pages/scraped-event/list';
import ScrapedEventDetailPage from './pages/scraped-event/detail';
import ActivityBugSolverPage from './pages/master-data/bug-solver';
import SubscriptionPlansPage from './pages/subscription/plan/list';
import SubscriptionPlanDetailPage from './pages/subscription/plan/detail';
import SubscriptionCodesPage from './pages/subscription/code/list';
import SubscriptionCodeDetailPage from './pages/subscription/code/detail';
import SportBrandsPage from './pages/brand/sport-brand/list';
import SportBrandDetailPage from './pages/brand/sport-brand/detail';
import BrandGearsPage from './pages/brand/brand-gear/list';
import BrandGearDetailPage from './pages/brand/brand-gear/detail';
import AdvanceShareStylesPage from './pages/advance-share/style/list';
import AdvanceShareStyleDetailPage from './pages/advance-share/style/detail';
import AdvanceShareStyleVersionDetailPage from './pages/advance-share/style/version/detail';
import AdvanceSharePacksPage from './pages/advance-share/pack/list';
import AdvanceSharePackDetailPage from './pages/advance-share/pack/detail';
import AdvanceShareTemplatesPage from './pages/advance-share/template/list';
import AdvanceShareTemplateDetailPage from './pages/advance-share/template/detail';
import PointTransactionsPage from './pages/point-transaction/list';
import PointTypesPage from './pages/point-type/list';
import PointTypeDetailPage from './pages/point-type/detail';
import PointAccountsPage from './pages/point-account/list';
import PointAccountDetailPage from './pages/point-account/detail';
import EnergyHubsPage from './pages/energy-hub/list';
import EnergyHubDetailPage from './pages/energy-hub/detail';
import EnergyHubReceiptsPage from './pages/energy-hub/receipt';
import EnergyHubRunRequestsPage from './pages/energy-hub/hub-run';
import PurchasesPage from './pages/marketplace/purchase/list';
import CommissionPoliciesPage from './pages/marketplace/commission-policy/list';
import GlobalActions from './pages/master-data/global-actions';
import { appNavs } from './config';
import { AppContextProvider } from './infra/AppContextProvider';
import ProtectedRoutes from './infra/ProtectedRoute';

const App = () => {

  return (
    <IntlProvider locale="en" messages={locales.en}>
      <AppContextProvider>
        <CustomProvider locale={enGB}>
          <Routes>
            <Route path="/login" element={<SignInPage />} />
            <Route element={<ProtectedRoutes />}>
              <Route path="/" element={<Frame navs={appNavs} />}>
                <Route index element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="continents" element={<ContinentsPage />} />
                <Route path="countries" element={<CountriesPage />} />
                <Route path="countries/new" element={<CountryDetailPage />} />
                <Route path="countries/:id" element={<CountryDetailPage />} />
                <Route path="cities" element={<CitiesPage />} />
                <Route path="cities/new" element={<CityDetailPage />} />
                <Route path="cities/:id" element={<CityDetailPage />} />
                <Route path="cities/districts/new" element={<CityDistrictDetailPage />} />
                <Route path="cities/districts/multiple/new" element={<CityMultipleDistrictPage />} />
                <Route path="cities/districts/:id" element={<CityDistrictDetailPage />} />
                <Route path="discovery-points" element={<DiscoveryPointsPage />} />
                <Route path="discovery-points/new" element={<DiscoveryPointDetailPage />} />
                <Route path="discovery-points/:id" element={<DiscoveryPointDetailPage />} />
                <Route path="routes" element={<RoutesPage />} />
                <Route path="routes/new" element={<RouteDetailPage />} />
                <Route path="routes/:id" element={<RouteDetailPage />} />
                <Route path="running-clubs" element={<RunningClubsPage />} />
                <Route path="running-clubs/new" element={<RunningClubDetailPage />} />
                <Route path="running-clubs/:id" element={<RunningClubDetailPage />} />
                <Route path="running-clubs/events" element={<RunningClubEventsPage />} />
                <Route path="running-clubs/tags" element={<ClubTagsPage />} />
                <Route path="running-clubs/events/new" element={<RunningClubEventDetailPage />} />
                <Route path="running-clubs/events/:id" element={<RunningClubEventDetailPage />} />
                <Route path="stories" element={<StoriesPage />} />
                <Route path="stories/new" element={<StoryDetailPage />} />
                <Route path="stories/:id" element={<StoryDetailPage />} />
                <Route path="stories/chapters/new" element={<StoryChapterDetailPage />} />
                <Route path="stories/chapters/:id" element={<StoryChapterDetailPage />} />
                <Route path="stories/chapters/medias/new" element={<StoryChapterMediaDetailPage />} />
                <Route path="stories/chapters/medias/:id" element={<StoryChapterMediaDetailPage />} />
                <Route path="stories/chapters/tasks/new" element={<StoryChapterTaskDetailPage />} />
                <Route path="stories/chapters/tasks/:id" element={<StoryChapterTaskDetailPage />} />
                <Route path="encyclopedia-items" element={<EncyclopediaItemsPage />} />
                <Route path="encyclopedia-items/new" element={<EncyclopediaItemDetailPage />} />
                <Route path="encyclopedia-items/:id" element={<EncyclopediaItemDetailPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/new" element={<UserDetailPage />} />
                <Route path="users/:id" element={<UserDetailPage />} />
                <Route path="activities" element={<ActivitiesPage />} />
                <Route path="activities/:id" element={<ActivityDetailPage />} />
                <Route path="activities/reports" element={<ActivityReportsPage />} />
                <Route path="activities/reports/:id" element={<ActivityReportDetailPage />} />
                <Route path="achievements" element={<AchievementsPage />} />
                <Route path="achievements/new" element={<AchievementDetailPage />} />
                <Route path="achievements/:id" element={<AchievementDetailPage />} />
                <Route path="run-events" element={<RunEventsPage />} />
                <Route path="run-events/new" element={<RunEventDetailPage />} />
                <Route path="run-events/:id" element={<RunEventDetailPage />} />
                <Route path="run-events/distances/:id" element={<RunEventDistanceDetailPage />} />
                <Route path="run-event-keys" element={<RunEventKeysPage />} />
                <Route path="scraped-events" element={<ScrapedEventsPage />} />
                <Route path="scraped-events/:id" element={<ScrapedEventDetailPage />} />
                <Route path="activity-bug-solver" element={<ActivityBugSolverPage />} />
                <Route path="subscription/plans" element={<SubscriptionPlansPage />} />
                <Route path="subscription/plans/new" element={<SubscriptionPlanDetailPage />} />
                <Route path="subscription/plans/:id" element={<SubscriptionPlanDetailPage />} />
                <Route path="subscription/codes" element={<SubscriptionCodesPage />} />
                <Route path="subscription/codes/new" element={<SubscriptionCodeDetailPage />} />
                <Route path="subscription/codes/:id" element={<SubscriptionCodeDetailPage />} />
                <Route path="sport-brands" element={<SportBrandsPage />} />
                <Route path="sport-brands/new" element={<SportBrandDetailPage />} />
                <Route path="sport-brands/:id" element={<SportBrandDetailPage />} />
                <Route path="brand-gears" element={<BrandGearsPage />} />
                <Route path="brand-gears/new" element={<BrandGearDetailPage />} />
                <Route path="brand-gears/:id" element={<BrandGearDetailPage />} />
                <Route path="advance-shares/styles" element={<AdvanceShareStylesPage />} />
                <Route path="advance-shares/styles/new" element={<AdvanceShareStyleDetailPage />} />
                <Route path="advance-shares/styles/:id" element={<AdvanceShareStyleDetailPage />} />
                <Route path="advance-shares/styles/versions/new" element={<AdvanceShareStyleVersionDetailPage />} />
                <Route path="advance-shares/styles/versions/:id" element={<AdvanceShareStyleVersionDetailPage />} />
                <Route path="advance-shares/packs" element={<AdvanceSharePacksPage />} />
                <Route path="advance-shares/packs/new" element={<AdvanceSharePackDetailPage />} />
                <Route path="advance-shares/packs/:id" element={<AdvanceSharePackDetailPage />} />
                <Route path="advance-shares/templates" element={<AdvanceShareTemplatesPage />} />
                <Route path="advance-shares/templates/:id" element={<AdvanceShareTemplateDetailPage />} />
                <Route path="point-transactions" element={<PointTransactionsPage />} />
                <Route path="point-types" element={<PointTypesPage />} />
                <Route path="point-types/new" element={<PointTypeDetailPage />} />
                <Route path="point-types/:id" element={<PointTypeDetailPage />} />
                <Route path="point-accounts" element={<PointAccountsPage />} />
                <Route path="point-accounts/new" element={<PointAccountDetailPage />} />
                <Route path="point-accounts/:id" element={<PointAccountDetailPage />} />
                <Route path="energy-hubs" element={<EnergyHubsPage />} />
                <Route path="energy-hubs/new" element={<EnergyHubDetailPage />} />
                <Route path="energy-hubs/:id" element={<EnergyHubDetailPage />} />
                <Route path="energy-hub-receipts" element={<EnergyHubReceiptsPage />} />
                <Route path="energy-hub-runs" element={<EnergyHubRunRequestsPage />} />
                <Route path="marketplace/purchases" element={<PurchasesPage />} />
                <Route path="marketplace/commission-policies" element={<CommissionPoliciesPage />} />
                <Route path="global-actions" element={<GlobalActions />} />
                <Route path="error-404" element={<Error404Page />} />
                <Route path="error-403" element={<Error403Page />} />
                <Route path="error-500" element={<Error500Page />} />
                <Route path="error-503" element={<Error503Page />} />                            
              </Route>
            </Route>
            <Route path="*" element={<Error404Page />} />
          </Routes>
        </CustomProvider>
      </AppContextProvider>
    </IntlProvider>
  );
};

export default App;
